import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, CircularProgress,
  Alert, Chip, IconButton, Tooltip, TablePagination, TextField,
  Radio, RadioGroup, FormControlLabel, FormControl, Collapse,
  Select, MenuItem, InputLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../store/languageStore';
import authService from '../shared/services/authService';
import AppHeader from '../shared/components/AppHeader';
import ModuleAuthModal from '../shared/components/ModuleAuthModal';

interface PdfData {
  relPdf?: string[];
  notPdf?: string[];
  extPdf?: string[];
}

interface StatusEntry {
  nachrichtennummer: string;
  datum: string;
  mrnlrn: string;
  empfanger: string;
  kennnummerDerSendung: string;
  subUser: string;
  status: string;
  pdf?: PdfData;
}

interface Filters {
  filterUser: 'current' | 'all';
  filterMrnlrn: string;
  filterDatum: string;
  filterStatus: string;
}

const T_PREFIX = 'features.exportStatusPage';

const STATUS_KEY_MAP: Record<string, string> = {
  'RISIKO': 'statusRisiko',
  'ÜBERLASSEN': 'statusUeberlassen',
  'AUSFUHRANZEIGE': 'statusAusfuhranzeige',
  'STORNO': 'statusStorno',
  'FEHLERMELDUNG': 'statusFehlermeldung',
  'STATUSMELDUNG': 'statusStatusmeldung',
};

const DEFAULT_FILTERS: Filters = {
  filterUser: 'current',
  filterMrnlrn: '',
  filterDatum: '',
  filterStatus: '',
};

function sanitizeFilename(name: string): string {
  return name.replace(/[/\\?%*:|"<>]/g, '_');
}

function downloadBase64Pdf(base64: string, filename: string): string | null {
  try {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
    return null;
  } catch {
    return 'Download failed';
  }
}

const PdfButtons: React.FC<{
  pdfs: string[] | undefined;
  prefix: string;
  label: string;
  mrnlrn: string;
  downloadFailedText: string;
}> = ({ pdfs, prefix, label, mrnlrn, downloadFailedText }) => {
  const [errors, setErrors] = useState<Record<number, string>>({});

  if (!pdfs || pdfs.length === 0) {
    return <Typography variant="body2" color="text.secondary">&mdash;</Typography>;
  }

  const safeName = sanitizeFilename(mrnlrn);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {pdfs.map((b64, idx) => (
        <Box key={idx} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={`${label} ${idx + 1}`}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={() => {
                const filename = `${prefix}_${safeName}_${idx + 1}.pdf`;
                const err = downloadBase64Pdf(b64, filename);
                if (err) {
                  setErrors(prev => ({ ...prev, [idx]: downloadFailedText }));
                }
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minHeight: 28 }}
            >
              {label} {idx + 1}
            </Button>
          </Tooltip>
          {errors[idx] && (
            <Typography variant="caption" color="error">{errors[idx]}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

const ExportStatusPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { tString } = useTranslation();
  const urlModuleId = searchParams.get('moduleId') || '';
  const [moduleId, setModuleId] = useState(urlModuleId);

  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorModuleName, setErrorModuleName] = useState('');
  const [statusData, setStatusData] = useState<StatusEntry[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filter state: "draft" holds what the user is typing, "applied" is what's sent to the API
  const [appliedFilters, setAppliedFilters] = useState<Filters>({ ...DEFAULT_FILTERS });
  const [draftFilters, setDraftFilters] = useState<Filters>({ ...DEFAULT_FILTERS });
  const [showFilters, setShowFilters] = useState(false);

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Resolve moduleId from localStorage or API if not in URL
  useEffect(() => {
    if (urlModuleId) {
      setModuleId(urlModuleId);
      return;
    }
    const stored = localStorage.getItem('selectedModuleId');
    if (stored) {
      setModuleId(stored);
      return;
    }
    authService.getModules().then((modules: any[]) => {
      const first = modules?.find((m: any) => m.enabled) || modules?.[0];
      if (first?.id) setModuleId(first.id);
    }).catch(() => {});
  }, [urlModuleId]);

  const fetchStatus = useCallback(async (pageNum: number, limit: number, filters: Filters) => {
    if (!moduleId) {
      setErrorCode('noModule');
      return;
    }

    setLoading(true);
    setErrorCode(null);

    try {
      const startLimit = pageNum * limit;
      const endLimit = startLimit + limit;
      const response = await authService.getExportStatus(moduleId, startLimit, endLimit, {
        filterUser: filters.filterUser,
        filterMrnlrn: filters.filterMrnlrn,
        filterDatum: filters.filterDatum,
        filterStatus: filters.filterStatus,
      });
      const entries = response?.exportsStatus?.statusData || [];
      setStatusData(entries);
      setTotalRows(response?.exportsStatus?.totalRows || entries.length);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error === 'authentication_expired') {
        setErrorCode('authExpired');
        setErrorModuleName(detail.module_name || 'module');
        setShowAuthModal(true);
      } else if (typeof detail === 'object' && detail?.error === 'not_authenticated') {
        setErrorCode('notAuthenticated');
        setErrorModuleName(detail.module_name || 'module');
        setShowAuthModal(true);
      } else if (typeof detail === 'string') {
        setErrorCode(detail);
      } else {
        setErrorCode('fetchError');
      }
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  const getErrorMessage = (): string | null => {
    if (!errorCode) return null;
    switch (errorCode) {
      case 'noModule':
        return tString(`${T_PREFIX}.noModule`);
      case 'authExpired':
        return tString(`${T_PREFIX}.authExpired`, { module: errorModuleName });
      case 'notAuthenticated':
        return tString(`${T_PREFIX}.notAuthenticated`, { module: errorModuleName });
      case 'fetchError':
        return tString(`${T_PREFIX}.fetchError`);
      default:
        return errorCode;
    }
  };

  // Initial fetch + refetch on page/rowsPerPage/appliedFilters change
  useEffect(() => {
    if (user && moduleId) {
      fetchStatus(page, rowsPerPage, appliedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, moduleId, page, rowsPerPage, appliedFilters]);

  if (!user) {
    navigate('/');
    return null;
  }

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedFilters({ ...draftFilters });
  };

  const handleClearFilters = () => {
    const cleared = { ...DEFAULT_FILTERS };
    setDraftFilters(cleared);
    setPage(0);
    setAppliedFilters(cleared);
  };

  const handleUserScopeChange = (value: 'current' | 'all') => {
    const updated = { ...draftFilters, filterUser: value };
    setDraftFilters(updated);
    // Apply immediately on radio change
    setPage(0);
    setAppliedFilters(updated);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const hasActiveTextFilters = appliedFilters.filterMrnlrn || appliedFilters.filterDatum || appliedFilters.filterStatus;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
      <AppHeader />
      <Container maxWidth="xl" sx={{ py: 2, flex: 1 }}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton onClick={() => navigate('/process')} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" fontWeight={600}>
                {tString(`${T_PREFIX}.title`)}
              </Typography>
              {totalRows > 0 && (
                <Chip
                  label={tString(`${T_PREFIX}.records`, { count: totalRows })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ textTransform: 'none' }}
                color={hasActiveTextFilters ? 'secondary' : 'primary'}
              >
                {tString(`${T_PREFIX}.filters`)}
                {hasActiveTextFilters ? ' *' : ''}
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={() => fetchStatus(page, rowsPerPage, appliedFilters)}
                disabled={loading || !moduleId}
                sx={{ textTransform: 'none' }}
              >
                {loading ? tString(`${T_PREFIX}.refreshing`) : tString(`${T_PREFIX}.refresh`)}
              </Button>
            </Box>
          </Box>

          {/* Filter Bar */}
          <Collapse in={showFilters}>
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1.5 }}
            >
              {/* Row 1: User scope radio */}
              <FormControl component="fieldset" sx={{ mb: 1.5 }}>
                <RadioGroup
                  row
                  value={draftFilters.filterUser}
                  onChange={(e) => handleUserScopeChange(e.target.value as 'current' | 'all')}
                >
                  <FormControlLabel
                    value="current"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" fontWeight={draftFilters.filterUser === 'current' ? 600 : 400}>
                        {tString(`${T_PREFIX}.currentUser`)}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" fontWeight={draftFilters.filterUser === 'all' ? 600 : 400}>
                        {tString(`${T_PREFIX}.allUsers`)}
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {/* Row 2: Text filters + Apply/Clear */}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label={tString(`${T_PREFIX}.filterByLrn`)}
                  value={draftFilters.filterMrnlrn}
                  onChange={(e) => setDraftFilters(prev => ({ ...prev, filterMrnlrn: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                  sx={{ minWidth: 220 }}
                  InputProps={{ sx: { fontSize: '0.875rem' } }}
                />
                <TextField
                  size="small"
                  label={tString(`${T_PREFIX}.filterByDate`)}
                  value={draftFilters.filterDatum}
                  onChange={(e) => setDraftFilters(prev => ({ ...prev, filterDatum: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                  placeholder="YYYYMMDD"
                  sx={{ minWidth: 180 }}
                  InputProps={{ sx: { fontSize: '0.875rem' } }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>
                    {tString(`${T_PREFIX}.filterByStatus`)}
                  </InputLabel>
                  <Select
                    value={draftFilters.filterStatus}
                    label={tString(`${T_PREFIX}.filterByStatus`)}
                    onChange={(e) => setDraftFilters(prev => ({ ...prev, filterStatus: e.target.value }))}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="">
                      <em>{tString(`${T_PREFIX}.allStatuses`)}</em>
                    </MenuItem>
                    <MenuItem value="RISIKO">{tString(`${T_PREFIX}.statusRisiko`)}</MenuItem>
                    <MenuItem value="ÜBERLASSEN">{tString(`${T_PREFIX}.statusUeberlassen`)}</MenuItem>
                    <MenuItem value="AUSFUHRANZEIGE">{tString(`${T_PREFIX}.statusAusfuhranzeige`)}</MenuItem>
                    <MenuItem value="STORNO">{tString(`${T_PREFIX}.statusStorno`)}</MenuItem>
                    <MenuItem value="FEHLERMELDUNG">{tString(`${T_PREFIX}.statusFehlermeldung`)}</MenuItem>
                    <MenuItem value="STATUSMELDUNG">{tString(`${T_PREFIX}.statusStatusmeldung`)}</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchIcon />}
                  onClick={handleApplyFilters}
                  sx={{ textTransform: 'none', height: 40 }}
                >
                  {tString(`${T_PREFIX}.applyFilters`)}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{ textTransform: 'none', height: 40 }}
                >
                  {tString(`${T_PREFIX}.clearFilters`)}
                </Button>
              </Box>
            </Paper>
          </Collapse>

          {/* Error */}
          {errorCode && (
            <Alert
              severity={errorCode === 'authExpired' || errorCode === 'notAuthenticated' ? 'warning' : 'error'}
              sx={{ mb: 2 }}
              onClose={() => setErrorCode(null)}
              action={
                (errorCode === 'authExpired' || errorCode === 'notAuthenticated') ? (
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => setShowAuthModal(true)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {tString(`${T_PREFIX}.authenticate`)}
                  </Button>
                ) : undefined
              }
            >
              {getErrorMessage()}
            </Alert>
          )}

          {/* Loading */}
          {loading && statusData.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }} color="text.secondary">
                {tString(`${T_PREFIX}.loading`)}
              </Typography>
            </Box>
          )}

          {/* Empty state */}
          {!loading && !errorCode && statusData.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {tString(`${T_PREFIX}.emptyTitle`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tString(`${T_PREFIX}.emptyDescription`)}
              </Typography>
            </Box>
          )}

          {/* Data table */}
          {statusData.length > 0 && (
            <>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 200 }}>
                        {tString(`${T_PREFIX}.messageNumber`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 140 }}>
                        {tString(`${T_PREFIX}.date`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 260 }}>
                        {tString(`${T_PREFIX}.mrnLrn`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 160 }}>
                        {tString(`${T_PREFIX}.recipient`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 180 }}>
                        {tString(`${T_PREFIX}.shipmentId`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 100 }}>
                        {tString(`${T_PREFIX}.status`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 160 }}>
                        {tString(`${T_PREFIX}.releasedPdfs`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 160 }}>
                        {tString(`${T_PREFIX}.notificationPdfs`)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', minWidth: 140 }}>
                        {tString(`${T_PREFIX}.extPdfs`)}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusData.map((entry, idx) => (
                      <TableRow key={`${entry.nachrichtennummer}-${idx}`} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {entry.nachrichtennummer || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.datum || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {entry.mrnlrn || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.empfanger || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {entry.kennnummerDerSendung || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.status ? (STATUS_KEY_MAP[entry.status] ? tString(`${T_PREFIX}.${STATUS_KEY_MAP[entry.status]}`) : entry.status) : '—'}</TableCell>
                        <TableCell>
                          <PdfButtons
                            pdfs={entry.pdf?.relPdf}
                            prefix="relPdf"
                            label={tString(`${T_PREFIX}.released`)}
                            mrnlrn={entry.mrnlrn || `row_${idx}`}
                            downloadFailedText={tString(`${T_PREFIX}.downloadFailed`)}
                          />
                        </TableCell>
                        <TableCell>
                          <PdfButtons
                            pdfs={entry.pdf?.notPdf}
                            prefix="notPdf"
                            label={tString(`${T_PREFIX}.notification`)}
                            mrnlrn={entry.mrnlrn || `row_${idx}`}
                            downloadFailedText={tString(`${T_PREFIX}.downloadFailed`)}
                          />
                        </TableCell>
                        <TableCell>
                          <PdfButtons
                            pdfs={entry.pdf?.extPdf}
                            prefix="extPdf"
                            label={tString(`${T_PREFIX}.external`)}
                            mrnlrn={entry.mrnlrn || `row_${idx}`}
                            downloadFailedText={tString(`${T_PREFIX}.downloadFailed`)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={tString(`${T_PREFIX}.rowsPerPage`)}
              />
            </>
          )}
        </Paper>
      </Container>

      {/* Module Authentication Modal */}
      <ModuleAuthModal
        open={showAuthModal}
        onClose={(authenticated) => {
          setShowAuthModal(false);
          if (authenticated) {
            setErrorCode(null);
            fetchStatus(page, rowsPerPage, appliedFilters);
          }
        }}
        moduleId={moduleId}
        moduleName={errorModuleName || 'Module'}
      />
    </Box>
  );
};

export default ExportStatusPage;
